import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Form, Button, Alert, Spinner } from 'react-bootstrap';
import ParticlesAuth from './ParticlesAuth';
import {
  verifyEmail,
  resendVerificationEmail,
  logout,
  clearError,
} from '../../features/auth/authSlice';
import { needsEmailVerification } from '../../utils/authVerification';

const OTP_LENGTH = 6;
const OTP_VALID_MINUTES = 15;

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token, verificationRequired, status, error: authError } = useSelector(
    (state) => state.auth
  );

  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [timeLeft, setTimeLeft] = useState(OTP_VALID_MINUTES * 60);

  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const otpExpiryRef = useRef(0);

  const email =
    user?.email || localStorage.getItem('auth_pending_email') || '';

  const startTimer = useCallback((seconds) => {
    otpExpiryRef.current = Date.now() + seconds * 1000;
    setTimeLeft(seconds);

    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((otpExpiryRef.current - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    startTimer(OTP_VALID_MINUTES * 60);
  }, [startTimer]);

  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken && !token) {
      navigate('/login', { replace: true });
      return;
    }

    if (user && !needsEmailVerification(user) && !verificationRequired) {
      navigate('/dashboard', { replace: true });
    }
  }, [token, user, verificationRequired, navigate]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otpValues];
    next[index] = digit;
    setOtpValues(next);
    setError('');
    dispatch(clearError());

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setOtpValues(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();

    const otp = otpValues.join('');
    if (otp.length !== OTP_LENGTH) {
      setError('Please enter the complete 6-digit verification code.');
      return;
    }

    setVerifying(true);
    setError('');
    setSuccess('');

    try {
      const result = await dispatch(verifyEmail(otp)).unwrap();
      setSuccess(result.message || 'Email verified successfully.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err?.type === 'account_verification_expired') {
        navigate('/register', { replace: true, state: { expired: true } });
        return;
      }
      setError(
        typeof err === 'string'
          ? err
          : err?.message || 'Invalid or expired verification code.'
      );
      setOtpValues(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (otpValues.every((v) => v) && !verifying) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValues]);

  const handleResend = async () => {
    if (resending) return;

    setResending(true);
    setError('');
    setSuccess('');
    dispatch(clearError());

    try {
      const result = await dispatch(resendVerificationEmail()).unwrap();
      setSuccess(result.message || 'A new verification code has been sent to your email.');
      setOtpValues(Array(OTP_LENGTH).fill(''));
      const minutes = result.otpExpiresInMinutes || OTP_VALID_MINUTES;
      startTimer(minutes * 60);
      inputRefs.current[0]?.focus();
    } catch (err) {
      if (err?.type === 'account_verification_expired') {
        navigate('/register', { replace: true, state: { expired: true } });
        return;
      }
      setError(
        typeof err === 'string'
          ? err
          : err?.message || 'Unable to resend verification code.'
      );
    } finally {
      setResending(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login', { replace: true });
  };

  const displayError =
    error ||
    (typeof authError === 'string' ? authError : authError?.message) ||
    '';

  return (
    <ParticlesAuth>
      <Col xxl={6} className="mx-auto">
        <Card className="mb-0 border-0 shadow-none mb-0">
          <Card.Body className="p-sm-5 m-lg-4">
            <div className="text-muted text-center mb-4 pb-2 mx-lg-3">
              <div className="mb-4">
                <i className="ri-mail-check-line display-4 text-primary" aria-hidden="true" />
              </div>
              <h4 className="text-dark">Verify Your Email</h4>
              <p className="mb-0">
                Enter the 6-digit code sent to{' '}
                <span className="fw-semibold">{email || 'your email'}</span>
              </p>
            </div>

            {success && (
              <Alert variant="success" className="mb-3">
                {success}
              </Alert>
            )}
            {displayError && (
              <Alert variant="danger" className="mb-3">
                {displayError}
              </Alert>
            )}

            <Alert variant="warning" className="mb-4 small">
              Your account will expire in 5 days if email verification is not completed.
            </Alert>

            <Form onSubmit={handleVerify} autoComplete="off">
              <Row className="justify-content-center g-2 mb-3">
                {otpValues.map((val, idx) => (
                  <Col xs={2} sm={2} key={idx}>
                    <Form.Control
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      className="form-control form-control-lg text-center bg-light border-light"
                      maxLength={1}
                      value={val}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      disabled={verifying || status === 'loading'}
                      autoFocus={idx === 0}
                      aria-label={`Digit ${idx + 1}`}
                    />
                  </Col>
                ))}
              </Row>

              {timeLeft > 0 && (
                <p className="text-center text-muted small mb-3">
                  Code expires in: {formatTime(timeLeft)}
                </p>
              )}

              <Button
                type="submit"
                className="btn btn-primary w-100"
                disabled={verifying || otpValues.some((v) => !v)}
              >
                {verifying && <Spinner size="sm" className="me-2" />}
                Confirm
              </Button>
            </Form>

            <div
              className="mt-4 text-center"
              onPaste={handlePaste}
            >
              <p className="mb-2 text-muted">Didn&apos;t receive a code?</p>
              <Button
                variant="link"
                className="p-0 fw-semibold text-primary text-decoration-underline"
                onClick={handleResend}
                disabled={resending}
              >
                {resending ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Sending...
                  </>
                ) : (
                  'Resend code'
                )}
              </Button>
            </div>

            <div className="mt-4 text-center">
              <Button
                variant="link"
                className="text-muted text-decoration-none"
                onClick={handleLogout}
              >
                Sign out
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </ParticlesAuth>
  );
};

export default VerifyEmail;
